/**
 * Pure function that computes drop validity for ALL columns at once.
 *
 * Rules (column = phase):
 * - Backward (any previous column): ALWAYS allowed → 'valid'
 * - Same column (reorder): → 'same-column'
 * - Forward one step from a manual phase: → 'valid'
 * - Forward from an auto-advance phase (autoAdvance + agents): → 'blocked'
 * - Forward more than one step: → 'blocked'
 */

export type DropState = 'valid' | 'blocked' | 'same-column';

export interface MinimalColumn {
  id: string;
  order: number;
  status: string;
}

export interface MinimalWorkflowPhase {
  name: string;
  autoAdvance: boolean;
  agents: string[];
  color?: string;
  disabled?: boolean;
}

/**
 * Returns the subset of columns where users may create new cards directly.
 *
 * Rule: include all non-auto-advance, non-disabled columns from the start up
 * to (but not including) the second auto-advance phase. This lets users target
 * manual intake phases (e.g. backlog, planning, plan-review) while preventing
 * card creation inside agent-driven pipeline phases.
 *
 * If there is only one auto-advance phase, all phases beyond it (up to end)
 * that are non-auto are also included. If there is no auto-advance phase, all
 * non-disabled columns are creatable.
 *
 * Generic over C so callers that pass full Column objects get full Column
 * objects back (no information is lost).
 */
export function getCreatableColumns<C extends MinimalColumn>(
  columns: C[],
  workflow?: MinimalWorkflowPhase[]
): C[] {
  const sorted = [...columns].sort((a, b) => a.order - b.order);

  if (!workflow || workflow.length === 0) {
    // No workflow info — only the first column is a safe default
    return sorted.slice(0, 1);
  }

  // Build a lookup: status name → phase info
  const phaseByName = new Map<string, MinimalWorkflowPhase>();
  for (const phase of workflow) {
    phaseByName.set(phase.name, phase);
  }

  // Find the index (in sorted columns) of each auto-advance column
  const autoIndexes: number[] = [];
  for (let i = 0; i < sorted.length; i++) {
    const phase = phaseByName.get(sorted[i].status);
    if (phase?.autoAdvance) {
      autoIndexes.push(i);
    }
  }

  // Determine the upper bound: stop before the second auto-advance column
  const cutoffIndex =
    autoIndexes.length >= 2
      ? autoIndexes[1]          // stop before second auto phase
      : sorted.length;          // no second auto phase — include everything to end

  // Collect columns before the cutoff: include non-disabled columns that are
  // either not auto-advance OR are the first auto-advance phase (users can
  // create a card in triage and have an agent pick it up immediately).
  const result: C[] = [];
  for (let i = 0; i < cutoffIndex; i++) {
    const col = sorted[i];
    const phase = phaseByName.get(col.status);
    const isFirstAuto = autoIndexes[0] === i;
    if ((!phase?.autoAdvance || isFirstAuto) && !phase?.disabled) {
      result.push(col);
    }
  }

  // Always include at least the first column so creation is never impossible
  if (result.length === 0 && sorted.length > 0) {
    result.push(sorted[0]);
  }

  return result;
}

export function computeDropStates(
  columns: MinimalColumn[],
  sourceColumnId: string,
  workflow?: MinimalWorkflowPhase[]
): Map<string, DropState> {
  const sorted = [...columns].sort((a, b) => a.order - b.order);
  const sourceIdx = sorted.findIndex((c) => c.id === sourceColumnId);
  const result = new Map<string, DropState>();

  if (sourceIdx === -1) {
    // Unknown source — mark everything blocked
    sorted.forEach((col) => result.set(col.id, 'blocked'));
    return result;
  }

  const sourceCol = sorted[sourceIdx];
  const isAuto = workflow
    ? workflow.some(
        (p) => p.name === sourceCol.status && p.autoAdvance
      )
    : false;

  for (let i = 0; i < sorted.length; i++) {
    const col = sorted[i];
    if (i === sourceIdx) {
      result.set(col.id, 'same-column');
    } else if (i < sourceIdx) {
      // Backward — always valid
      result.set(col.id, 'valid');
    } else {
      // Forward
      if (isAuto) {
        result.set(col.id, 'blocked');
      } else if (i === sourceIdx + 1) {
        result.set(col.id, 'valid');
      } else {
        result.set(col.id, 'blocked');
      }
    }
  }

  return result;
}
